import { useQuery } from '@tanstack/react-query';
import { ClipboardCopy, Download } from 'lucide-react';
import { useMemo, useState } from 'react';

import { listEngagementsForCustomer } from '@/api/engagements';
import { listFindingsForRun } from '@/api/findings';
import { listRunsForEngagement } from '@/api/runs';
import type { AuditRunOut } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { useTransactionDocuments } from '@/hooks/useTransactionDocuments';
import { formatCurrency, formatDateTime } from '@/lib/format';
import {
  groupBySupplier,
  missingInvoiceRows,
  missingInvoicesCsv,
} from '@/lib/missingInvoices';

import { useAuth } from '../auth/AuthContext';

/** The invoice-collection worklist: every dispositioned-toward-recovery
 *  finding (approved / needs_review) on the
 *  customer's latest completed run whose transaction has no paper
 *  attached — grouped by supplier, biggest recovery first. This is the
 *  list that goes to the customer's finance team; the CSV export is
 *  the email attachment.
 */
export function MissingInvoicesPanel({ slug }: { slug: string }) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const [copied, setCopied] = useState<'copied' | 'failed' | null>(null);

  // Latest completed run across the customer's engagements — the
  // "current truth" the collection list is drawn from.
  const latestRun = useQuery({
    queryKey: ['latest-completed-run', slug, userKey],
    enabled: isSignedIn,
    queryFn: async (): Promise<AuditRunOut | null> => {
      const token = (await getIdToken()) ?? '';
      const engagements = await listEngagementsForCustomer(slug, token);
      const runLists = await Promise.all(
        engagements.items.map((e) => listRunsForEngagement(e.id, token)),
      );
      const completed = runLists
        .flatMap((l) => l.items)
        .filter((r) => r.status === 'completed');
      completed.sort((a, b) => (a.started_at < b.started_at ? 1 : -1));
      return completed[0] ?? null;
    },
  });

  const findings = useQuery({
    queryKey: ['findings', 'by-run', latestRun.data?.id, userKey],
    enabled: isSignedIn && latestRun.data != null,
    queryFn: async () =>
      listFindingsForRun(latestRun.data!.id, (await getIdToken()) ?? ''),
  });

  const transactionIds = useMemo(
    () => (findings.data?.items ?? []).map((f) => f.transaction_id),
    [findings.data],
  );
  const { linksByTransaction, markedTransactions, linksReady, linksError } =
    useTransactionDocuments(slug, transactionIds);

  const groups = useMemo(() => {
    if (!findings.data || !linksReady || linksError) return [];
    return groupBySupplier(
      missingInvoiceRows(findings.data.items, linksByTransaction, markedTransactions),
    );
  }, [findings.data, linksByTransaction, markedTransactions, linksReady, linksError]);

  const totals = useMemo(
    () => ({
      transactions: groups.reduce((acc, g) => acc + g.rows.length, 0),
      recovery: groups.reduce((acc, g) => acc + g.totalRecovery, 0),
    }),
    [groups],
  );

  if (latestRun.isPending) {
    return <p className="text-sm text-brand-slate">Loading collection worklist…</p>;
  }
  if (latestRun.isError || findings.isError) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t load the collection worklist — reload to retry.
      </p>
    );
  }
  if (latestRun.data == null) {
    return (
      <p className="text-sm text-brand-slate">
        No completed run yet — the collection worklist draws from the latest run&apos;s
        findings.
      </p>
    );
  }
  if (linksError) {
    return (
      <p className="text-sm text-destructive">
        Invoice lookup failed — the worklist can&apos;t compute. Reload to retry.
      </p>
    );
  }
  if (findings.isPending || !linksReady) {
    return <p className="text-sm text-brand-slate">Loading collection worklist…</p>;
  }

  const csv = missingInvoicesCsv(groups);
  const download = () => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}-missing-invoices.csv`;
    // WebKit ignores click() on a detached download anchor.
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(csv);
      setCopied('copied');
    } catch {
      setCopied('failed');
    }
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <section className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-brand-navy">Missing invoices</h3>
          <p className="text-xs text-brand-slate">
            {totals.transactions} transaction{totals.transactions === 1 ? '' : 's'} ·{' '}
            {formatCurrency(String(totals.recovery))} at stake · from the run started{' '}
            {formatDateTime(latestRun.data.started_at)}
          </p>
        </div>
        {totals.transactions > 0 && (
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => void copy()}>
              <ClipboardCopy className="mr-1 h-4 w-4" aria-hidden="true" />
              {copied === 'copied' ? 'Copied' : copied === 'failed' ? 'Copy failed' : 'Copy CSV'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={download}>
              <Download className="mr-1 h-4 w-4" aria-hidden="true" />
              Download
            </Button>
          </div>
        )}
      </div>

      {totals.transactions === 0 ? (
        <p className="rounded-md border border-emerald-500/40 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-900">
          Nothing to collect — invoices are attached or marked unavailable.
        </p>
      ) : (
        <div className="grid gap-3">
          {groups.map((group) => (
            <div
              key={group.supplierName ?? '(no supplier)'}
              className="rounded-md border border-border"
            >
              <div className="flex items-baseline justify-between gap-3 border-b border-border/60 bg-muted/30 px-3 py-1.5">
                <p className="text-sm font-medium text-brand-navy">
                  {group.supplierName ?? '(no supplier)'}
                </p>
                <p className="font-mono text-xs text-brand-slate">
                  {formatCurrency(String(group.totalRecovery))} · {group.rows.length}{' '}
                  invoice{group.rows.length === 1 ? '' : 's'}
                </p>
              </div>
              <Table bordered={false}>
                <TableBody className="[&_td]:text-xs">
                  {group.rows.map((row) => (
                    <TableRow key={row.externalTransactionId} className="border-border/40">
                      <TableCell className="px-3 py-1.5 font-mono text-brand-slate">
                        {row.externalTransactionId}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-brand-slate">{row.billDate ?? '—'}</TableCell>
                      <TableCell className="px-3 py-1.5 text-right font-mono text-brand-navy">
                        {row.billAmount ?? '—'}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-right font-mono text-emerald-700">
                        {formatCurrency(String(row.recoveryAtStake))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
